<template>
  <div
    :class="[
      'sidebar-area position-fixed start-0 top-0 bg-black h-100vh transition',
      { active: stateStoreInstance.open },
    ]"
    id="sidebar-area"
  >
    <div class="sidebar-menu">
      <div class="logo position-absolute start-0 end-0 top-0 division-company-name">
        <img src="../../assets/images/home/logo-transparent.png" alt="Fin" width="42" />
        <span class="company-name fw-bold ms-10 fs-16">Fin</span>
        <div class="bb-2"></div>
        <button class="sidebar-burger-menu position-absolute lh-1 bg-transparent p-0 border-0" @click="stateStoreInstance.onChange">
          <i class="ph ph-x"></i>
        </button>
      </div>

      <ul class="sidebar-navbar-nav ps-0 mb-0 list-unstyled accordion" id="sidebarNavAccordion">
        <li v-for="item in visibleItems" :key="item.key" @click="stateStoreInstance.onChange" class="sidebar-nav-item accordion-item bg-transparent border-0 rounded-0">
          <router-link
            :to="item.path"
            class="accordion-button collapsed rounded-0 shadow-none bg-transparent d-block"
            :aria-current="isActive(item.path) ? 'page' : undefined"
          >
            <div :class="{ active: isActive(item.path) }">
              <i :class="item.icon"></i>
              <span class="title">{{ item.label }}</span>
            </div>
          </router-link>
        </li>

        <li @click="openSupportWhatsapp" class="sidebar-nav-item accordion-item bg-transparent border-0 rounded-0">
          <button type="button" class="accordion-button collapsed rounded-0 shadow-none bg-transparent d-block support-menu-button">
            <div>
              <i class="ph ph-whatsapp-logo"></i>
              <span class="title">Suporte</span>
            </div>
          </button>
        </li>
      </ul>
    </div>
  </div>

  <div class="mobile-bottom-menu">
    <nav
      class="mobile-bottom-nav"
      aria-label="Menu principal mobile"
      :style="{ gridTemplateColumns: `repeat(${mobilePrimaryItems.length + 1}, minmax(0, 1fr))` }"
    >
      <router-link
        v-for="item in mobilePrimaryItems"
        :key="item.key"
        :to="item.path"
        class="mobile-bottom-link"
        :class="{ active: isActive(item.path) }"
        :aria-current="isActive(item.path) ? 'page' : undefined"
      >
        <span class="mobile-bottom-icon"><i :class="item.icon"></i></span>
        <span class="mobile-bottom-label">{{ item.mobileLabel || item.label }}</span>
      </router-link>
      <button
        type="button"
        class="mobile-bottom-link mobile-more-button"
        :class="{ active: moreMenuOpen || isMoreRouteActive }"
        aria-label="Ver mais opções"
        aria-controls="mobileMoreMenu"
        :aria-expanded="moreMenuOpen"
        @click="moreMenuOpen = true"
      >
        <span class="mobile-bottom-icon"><i class="ph ph-dots-three"></i></span>
        <span class="mobile-bottom-label">Ver mais</span>
      </button>
    </nav>
  </div>

  <Teleport to="body">
    <div
      v-if="moreMenuOpen"
      class="mobile-more-backdrop"
      aria-hidden="true"
      @click="closeMoreMenu"
    ></div>
    <section
      v-if="moreMenuOpen"
      id="mobileMoreMenu"
      class="mobile-more-sheet"
      role="dialog"
      aria-modal="true"
      aria-labelledby="mobileMoreMenuTitle"
      @keydown.esc="closeMoreMenu"
    >
      <div class="mobile-more-sheet__handle" aria-hidden="true"></div>
      <div class="mobile-more-sheet__header">
        <div>
          <span class="mobile-more-sheet__eyebrow">Navegação</span>
          <h2 id="mobileMoreMenuTitle">Ver mais</h2>
        </div>
        <button type="button" class="mobile-more-sheet__close" aria-label="Fechar" @click="closeMoreMenu">
          <i class="ph ph-x"></i>
        </button>
      </div>

      <div class="mobile-more-sheet__actions">
        <router-link
          v-for="item in moreNavigationItems"
          :key="item.key"
          :to="item.path"
          class="mobile-more-sheet__action"
          @click="closeMoreMenu"
        >
          <span class="mobile-more-sheet__action-icon"><i :class="item.icon"></i></span>
          <span>
            <strong>{{ item.moreLabel || item.label }}</strong>
            <small>{{ item.moreDescription }}</small>
          </span>
          <i class="ph ph-caret-right mobile-more-sheet__arrow"></i>
        </router-link>

        <button type="button" class="mobile-more-sheet__action is-danger" @click="requestLogout">
          <span class="mobile-more-sheet__action-icon"><i class="flaticon-logout"></i></span>
          <span>
            <strong>Sair</strong>
            <small>Encerrar a sessão neste dispositivo</small>
          </span>
          <i class="ph ph-caret-right mobile-more-sheet__arrow"></i>
        </button>
      </div>
    </section>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import stateStore from "../../utils/store";
import { mapGetters } from "vuex";
import { canAccessPage as hasPageAccess } from "../../utils/accessControl";

interface NavigationItem {
  key: string;
  label: string;
  mobileLabel: string;
  path: string;
  icon: string;
  moreLabel?: string;
  moreDescription?: string;
}

export default defineComponent({
  name: "MainSidebar",
  emits: ["request-logout"],
  data() {
    return {
      moreMenuOpen: false,
      items: [
        { key: "dashboard", label: "Dashboard", mobileLabel: "Início", path: "/dashboard", icon: "flaticon-pie-chart-1" },
        { key: "transactions", label: "Lançamentos", mobileLabel: "Gastos", path: "/financial-management", icon: "flaticon-money" },
        { key: "recurring-expenses", label: "Recorrentes", mobileLabel: "Recorrentes", path: "/recurring-expenses", icon: "flaticon-calendar" },
        { key: "ai-advisor", label: "Planejamento IA", mobileLabel: "Planejar", path: "/financial-advisor", icon: "ph ph-sparkle" },
        { key: "goals", label: "Metas", mobileLabel: "Metas", path: "/financial-goals", icon: "ph ph-target", moreDescription: "Cadastre e acompanhe suas conquistas" },
        { key: "configurations", label: "Configurações", mobileLabel: "Config", path: "/configurations", icon: "flaticon-setting", moreDescription: "Preferências e dados da conta" },
        { key: "payment", label: "Pagamentos", mobileLabel: "Planos", path: "/payment", icon: "flaticon-credit-card", moreLabel: "Planos", moreDescription: "Gerencie sua assinatura" },
      ],
    };
  },
  setup() {
    return { stateStoreInstance: stateStore };
  },
  computed: {
    ...mapGetters(["getAllowedPages"]),
    visibleItems(): NavigationItem[] {
      return (this.items as NavigationItem[]).filter((item) => this.canAccessPage(item.key));
    },
    mobilePrimaryItems(): NavigationItem[] {
      return this.visibleItems.filter((item) => !["payment", "configurations", "goals"].includes(item.key));
    },
    moreNavigationItems(): NavigationItem[] {
      return this.visibleItems.filter((item) => ["goals", "configurations", "payment"].includes(item.key));
    },
    isMoreRouteActive(): boolean {
      return this.moreNavigationItems.some((item) => this.isActive(item.path));
    },
  },
  watch: {
    "$route.path"() {
      this.closeMoreMenu();
    },
  },
  methods: {
    canAccessPage(pageKey: string) {
      return hasPageAccess(this.getAllowedPages, pageKey);
    },
    isActive(path: string) {
      return this.$route.path === path;
    },
    closeMoreMenu() {
      this.moreMenuOpen = false;
    },
    requestLogout() {
      this.closeMoreMenu();
      this.$emit("request-logout");
    },
    openSupportWhatsapp() {
      stateStore.onChange();
      window.open("https://wa.me/5511915100084", "_blank", "noopener,noreferrer");
    },
  },
});
</script>

<style scoped>
.sidebar-area {
  background:
    radial-gradient(circle at 35% 5%, rgba(6, 180, 138, 0.14), transparent 25%),
    linear-gradient(180deg, #171629 0%, #10101e 100%) !important;
  box-shadow: 12px 0 36px rgba(18, 17, 32, 0.08);
}

#sidebar-area .division-company-name {
  align-items: center;
  background: transparent !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important;
  display: flex;
  min-height: 92px;
}

.division-company-name img {
  background: rgba(255, 255, 255, 0.07);
  border-radius: 11px;
  box-sizing: content-box;
  padding: 6px;
  width: 42px;
}

.company-name {
  color: #ffffff;
  font-size: 26px !important;
  letter-spacing: -0.02em;
}

.sidebar-navbar-nav {
  display: grid;
  gap: 8px;
  padding-top: 12px;
}

.sidebar-nav-item {
  margin-bottom: 0 !important;
}

.sidebar-nav-item .accordion-button {
  color: #aaa9ba !important;
  font-size: 14px !important;
  font-weight: 700 !important;
  min-height: 50px;
  padding: 0 !important;
}

.sidebar-nav-item .accordion-button::after {
  display: none !important;
}

.sidebar-nav-item .accordion-button > div {
  align-items: center;
  border-radius: 14px;
  display: flex;
  gap: 13px;
  min-height: 50px;
  padding: 0 14px;
  position: relative;
  transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
  width: 100%;
}

.sidebar-nav-item .accordion-button > div::before {
  background: #48d6ad;
  border-radius: 0 999px 999px 0;
  content: "";
  height: 24px;
  left: -20px;
  opacity: 0;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  transition: opacity 0.2s ease;
  width: 4px;
}

.sidebar-nav-item .accordion-button > div.active {
  background: rgba(6, 180, 138, 0.15);
  color: #ffffff;
}

.sidebar-nav-item .accordion-button > div.active::before {
  opacity: 1;
}

.sidebar-nav-item .accordion-button:hover > div {
  background: rgba(255, 255, 255, 0.06);
  color: #ffffff;
  transform: translateX(2px);
}

.sidebar-nav-item .accordion-button i {
  font-size: 20px !important;
  left: auto !important;
  line-height: 1 !important;
  position: static !important;
  transform: none !important;
}

.support-menu-button {
  border: 0;
  color: #aaa9ba !important;
  min-height: 50px;
  padding: 0 !important;
  text-align: left;
}

.support-menu-button:hover {
  color: #ffffff !important;
}

.mobile-bottom-menu {
  display: none;
}

@media (min-width: 1200px) {
  .sidebar-area.active .sidebar-nav-item .accordion-button > div {
    justify-content: center;
    padding: 0;
  }

  .sidebar-area.active:hover .sidebar-nav-item .accordion-button > div {
    justify-content: flex-start;
    padding: 0 14px;
  }

  .sidebar-area.active .sidebar-nav-item .accordion-button > div::before {
    left: -19px;
  }
}

@media (max-width: 767px) {
  .sidebar-area {
    display: none;
  }

  .mobile-bottom-menu {
    bottom: 0;
    display: block;
    left: 0;
    padding: 0 10px max(8px, env(safe-area-inset-bottom));
    pointer-events: none;
    position: fixed;
    right: 0;
    z-index: 1000;
  }

  .mobile-bottom-nav {
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid rgba(23, 22, 41, 0.08);
    border-radius: 21px;
    box-shadow: 0 12px 38px rgba(23, 22, 41, 0.18);
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    min-height: 68px;
    padding: 5px;
    pointer-events: auto;
  }

  .mobile-bottom-link {
    align-items: center;
    border: 0;
    border-radius: 15px;
    color: #858494;
    display: flex;
    flex-direction: column;
    font-size: 10px;
    font-weight: 700;
    gap: 3px;
    justify-content: center;
    min-height: 56px;
    min-width: 0;
    padding: 5px 2px 4px;
    position: relative;
    text-align: center;
    text-decoration: none;
    transition: background-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
  }

  .mobile-bottom-link.active {
    background: #e8f8f3;
    color: #05785d;
  }

  .mobile-more-button {
    background: transparent;
  }

  .mobile-more-button:focus-visible {
    box-shadow: 0 0 0 2px rgba(5, 120, 93, 0.3);
    outline: none;
  }

  .mobile-bottom-link:active {
    transform: scale(0.96);
  }

  .mobile-bottom-icon {
    align-items: center;
    display: inline-flex;
    height: 25px;
    justify-content: center;
    width: 25px;
  }

  .mobile-bottom-icon i {
    font-size: 21px;
    line-height: 1;
  }

  .mobile-bottom-link.active .mobile-bottom-icon i {
    font-size: 22px;
  }

  .mobile-bottom-label {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    width: 100%;
  }

}

.mobile-more-backdrop {
  background: rgba(17, 24, 39, 0.48);
  inset: 0;
  position: fixed;
  z-index: 1040;
}

.mobile-more-sheet {
  background: #ffffff;
  border-radius: 24px 24px 0 0;
  bottom: 0;
  box-shadow: 0 -18px 50px rgba(17, 24, 39, 0.18);
  left: 0;
  padding: 10px 18px max(22px, calc(14px + env(safe-area-inset-bottom)));
  position: fixed;
  right: 0;
  z-index: 1041;
}

.mobile-more-sheet__handle {
  background: #d9dde5;
  border-radius: 999px;
  height: 4px;
  margin: 0 auto 16px;
  width: 42px;
}

.mobile-more-sheet__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
}

.mobile-more-sheet__eyebrow {
  color: #8a93a3;
  display: block;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.12em;
  margin-bottom: 2px;
  text-transform: uppercase;
}

.mobile-more-sheet__header h2 {
  color: #171629;
  font-size: 20px;
  font-weight: 800;
  margin: 0;
}

.mobile-more-sheet__close {
  align-items: center;
  background: #f2f4f7;
  border: 0;
  border-radius: 50%;
  color: #596273;
  display: flex;
  font-size: 18px;
  height: 38px;
  justify-content: center;
  width: 38px;
}

.mobile-more-sheet__actions {
  display: grid;
  gap: 10px;
}

.mobile-more-sheet__action {
  align-items: center;
  background: #f7f8fa;
  border: 1px solid #edf0f4;
  border-radius: 16px;
  color: #343849;
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) 20px;
  min-height: 68px;
  padding: 10px 13px;
  text-align: left;
  text-decoration: none;
  width: 100%;
}

.mobile-more-sheet__action-icon {
  align-items: center;
  background: #e8f8f3;
  border-radius: 13px;
  color: #05785d;
  display: flex;
  font-size: 21px;
  height: 40px;
  justify-content: center;
  width: 40px;
}

.mobile-more-sheet__action strong,
.mobile-more-sheet__action small {
  display: block;
}

.mobile-more-sheet__action strong {
  font-size: 14px;
  font-weight: 800;
}

.mobile-more-sheet__action small {
  color: #8991a0;
  font-size: 11px;
  margin-top: 2px;
}

.mobile-more-sheet__action.is-danger {
  color: #b33a35;
}

.mobile-more-sheet__action.is-danger .mobile-more-sheet__action-icon {
  background: #fff0ef;
  color: #c64b45;
}

.mobile-more-sheet__arrow {
  color: #a0a7b3;
  font-size: 16px;
}

@media (prefers-reduced-motion: reduce) {
  .sidebar-nav-item .accordion-button > div,
  .mobile-bottom-link {
    transition: none;
  }
}
</style>

<style>
@media (max-width: 767px) {
  body.dark .mobile-bottom-nav {
    background: rgba(35, 34, 51, 0.97);
    border-color: rgba(255, 255, 255, 0.08);
  }

  body.dark .mobile-bottom-link {
    color: #aaa9ba;
  }

  body.dark .mobile-bottom-link.active {
    background: rgba(6, 180, 138, 0.17);
    color: #66e0bd;
  }

  body.dark .mobile-more-sheet {
    background: #171629;
  }

  body.dark .mobile-more-sheet__handle {
    background: #4b4a5d;
  }

  body.dark .mobile-more-sheet__header h2 {
    color: #ffffff;
  }

  body.dark .mobile-more-sheet__close,
  body.dark .mobile-more-sheet__action {
    background: #232233;
    border-color: rgba(255, 255, 255, 0.08);
    color: #e5e4eb;
  }

  body.dark .mobile-more-sheet__action.is-danger {
    color: #ffaaa4;
  }
}
</style>
